import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownItem, DropdownMenu, Icon } from "semantic-ui-react";

import { Dropdown, InterventionModal, useCan } from "components";
import { useShowPriceInVat } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import "modules/AppointmentDetails/components/CustomerActions/components/CheckInOptionalServiceActionSelector/CheckInOptionalServiceActionSelector.scss";
import { useAppointmentDealer, useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { canWriteBackCustomerData, generateDMSNr } from "utils/appointmentUtils";
import { getDisplayedPrice } from "utils/common";

import type { Appointment, CheckInResult } from "models";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";

type CheckInOptionalServiceActionSelectorProps = {
  result: CheckInResult;
  appointment: Appointment;
  onInterventionConverted: () => void;
};

export const CheckInOptionalServiceActionSelector = ({ result, appointment, onInterventionConverted }: CheckInOptionalServiceActionSelectorProps) => {
  const { t } = useTranslation();

  const appointmentLocation = useAppointmentLocation(appointment);
  const appointmentDealer = useAppointmentDealer(appointment);

  const canCreateIntervention = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Interventions);
  const showPriceInVat = useShowPriceInVat();

  const [convertToInterventionPayload, setConvertToInterventionPayload] = useState<AddInterventionPayload | null>(null);

  const isConvertedToIntervention = appointment.interventions?.some(intervention => intervention.check_in_result_id === result.id);
  const canConvertToIntervention = canCreateIntervention && appointmentLocation?.convert_checklist_to_intervention_is_enabled && !isConvertedToIntervention;

  const handleConvertToIntervention = () => {
    setConvertToInterventionPayload({
      dms_nr: generateDMSNr(),
      title: result.name,
      appointment_id: appointment.id,
      customer_ok: true,
      description: result.description,
      price: getDisplayedPrice(result.price, appointmentLocation, showPriceInVat),
      check_in_result_id: result.id
    });
  };

  return (
    <>
      <div className="CommunicationRemarkActionSelector">
        <Dropdown
          disabled={!canConvertToIntervention}
          className="action-dropdown"
          floating
          icon={null}
          direction="left"
          trigger={<Icon className={!canConvertToIntervention ? "link horizontal grey" : "plus grey"} />}
        >
          <DropdownMenu className="dropdown-menu">
            {canConvertToIntervention && (
              <DropdownItem onClick={handleConvertToIntervention}>
                <Icon className="screwdriver wrench" />
                {t("v8_convert_to_intervention", "Convert to Intervention")}
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>
      <InterventionModal
        appointmentId={appointment.id}
        carId={appointment.car_id}
        location={appointmentLocation}
        dealer={appointmentDealer}
        intervention={convertToInterventionPayload}
        open={convertToInterventionPayload !== null}
        onClose={() => setConvertToInterventionPayload(null)}
        onInterventionCreated={onInterventionConverted}
        canWriteBackCustomerData={canWriteBackCustomerData(appointmentLocation)}
      />
    </>
  );
};
